import { faCalendarDays, faClose, faFingerprint, faPenToSquare, faUserClock } from "@fortawesome/free-solid-svg-icons"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { MetricCard } from "../../../components/MetricCard/MetricCard"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useTimeClock } from "../../../hooks/TimeClock/useTimeClock"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { getTimeClockEntryCard } from "../../../Objects/TimeClock/TimeClockCards"
import type { TimeClockReport } from "../../../Objects/TimeClock/TimeClockReport"
import type { TimeClockLiveEmployee } from "../../../Objects/TimeClock/TimeClockLiveEmployee"
import type { TimeClockEmployeeSummary } from "../../../Objects/TimeClock/TimeClockEmployeeSummary"
import type { TimeClockEntry } from "../../../Objects/TimeClock/TimeClockEntry"
import type { TimeClockScheduleModelType } from "../../../Objects/TimeClock/TimeClockScheduleModelType"
import { formatWorkedMinutes } from "../../../Objects/TimeClock/TimeClockSettings"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { getDateKeyInTimeZone } from "../../../utils/timeZone"
import GetSpace from "../../../utils/space"
import { TimeClockEmployeeEntriesPopup } from "./TimeClockEmployeeEntriesPopup"
import { TimeClockPeriodFilterPopup } from "../../TimeClock/TimeClockPeriodFilterPopup"

interface TimeClockEmployeePopupProps {
  employee: TimeClockEmployeeSummary
  live?: TimeClockLiveEmployee
  scheduleModel: TimeClockScheduleModelType
  onClose: () => void
  onEditSchedule: () => void
  onEditPin: () => void
  onCreateAdjustment: () => void
  onReviewed: () => Promise<void>
}

const formatDateKey = (dateKey: string) => `${dateKey.slice(8, 10)}/${dateKey.slice(5, 7)}/${dateKey.slice(0, 4)}`

export const TimeClockEmployeePopup: React.FC<TimeClockEmployeePopupProps> = ({
  employee,
  live,
  scheduleModel,
  onClose,
  onEditSchedule,
  onEditPin,
  onCreateAdjustment,
  onReviewed,
}) => {
  const { t, i18n } = useTranslation(["timeClock", "helpPopup"])

  const { isLoading, getReport, reviewEntry, downloadReportPdf } = useTimeClock()

  const [report, setReport] = useState<TimeClockReport | undefined>(undefined)

  const [startDate, setStartDate] = useState(
    () => `${getDateKeyInTimeZone(new Date(), companyMemory.timeZoneId).slice(0, 8)}01`,
  )

  const [endDate, setEndDate] = useState(() => getDateKeyInTimeZone(new Date(), companyMemory.timeZoneId))

  const [isPeriodFilterOpen, setIsPeriodFilterOpen] = useState(false)

  const [isEntriesOpen, setIsEntriesOpen] = useState(false)

  const load = useCallback(async () => {
    const result = await getReport(employee.employeeId, startDate, endDate)

    if (result) setReport(result)
  }, [employee.employeeId, endDate, getReport, startDate])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  const entries: TimeClockEntry[] = useMemo(() => report?.days.flatMap((day) => day.entries) ?? [], [report])

  const pendingEntries = useMemo(() => entries.filter((entry) => entry.status === "Pending"), [entries])

  const cards = useMemo(
    () => entries.map((entry, index) => getTimeClockEntryCard(entry, index, t, i18n.language)),
    [entries, t, i18n.language],
  )

  const handleReviewEntry = async (entry: TimeClockEntry, isApproved: boolean) => {
    const reviewed = await reviewEntry(entry.id, { isApproved })

    if (!reviewed) return

    await load()

    await onReviewed()
  }

  const scheduledDays = employee.schedules.filter((schedule) => !schedule.isDayOff).length

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={employee.employeeName}
              icon={faUserClock}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-employee"
                    title={t("helpPopup:global.timeClockEmployee.title")}
                    description={t("helpPopup:global.timeClockEmployee.description")}
                    bullets={t("helpPopup:global.timeClockEmployee.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="flex flex-col gap-3 p-2">
              <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${
                      live?.isWorking ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {live?.isWorking ? t("management.working_now") : t("management.not_working")}
                  </span>

                  <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold text-cyan-700">
                    {employee.hasPin ? t("management.has_pin") : t("management.without_pin")}
                  </span>

                  <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                    {scheduledDays === 0
                      ? t("management.without_schedule")
                      : scheduledDays === 1
                        ? t("management.schedule_days_one", { count: scheduledDays })
                        : t("management.schedule_days_other", { count: scheduledDays })}
                  </span>

                  <button
                    type="button"
                    onClick={() => setIsPeriodFilterOpen(true)}
                    className="rounded-full border-2 border-orange-200 bg-white px-3 py-1 text-xs font-bold text-orange-700 shadow-sm hover:bg-orange-50"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.openPeriodFilter"
                  >
                    {formatDateKey(startDate)} – {formatDateKey(endDate)}
                  </button>
                </div>
              </Animate>

              {report && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <MetricCard
                    title={t("management.total_worked")}
                    value={formatWorkedMinutes(report.totalWorkedMinutes)}
                    icon={faUserClock}
                  />

                  <MetricCard
                    title={t("management.total_expected")}
                    value={formatWorkedMinutes(report.totalExpectedMinutes)}
                    icon={faCalendarDays}
                  />

                  <MetricCard
                    title={t("management.total_overtime")}
                    value={formatWorkedMinutes(report.totalOvertimeMinutes)}
                    icon={faPenToSquare}
                  />

                  <MetricCard
                    title={t("management.total_shortage")}
                    value={formatWorkedMinutes(report.totalShortageMinutes)}
                    icon={faFingerprint}
                  />
                </div>
              )}

              {report && report.openPeriodCount > 0 && (
                <Animate type={AnimationTypes.slideUpIn} duration="1.7s">
                  <p className="rounded-xl border border-amber-300 bg-amber-50 p-2 text-xs font-medium text-amber-900">
                    {t("management.open_period_warning")}
                  </p>
                </Animate>
              )}

              {pendingEntries.length > 0 && (
                <Animate type={AnimationTypes.slideUpIn} duration="1.8s">
                  <div className="flex flex-col gap-2 rounded-xl border border-amber-300 bg-amber-50 p-2">
                    <p className="text-xs font-bold text-amber-900">{t("management.pending_warning")}</p>

                    {pendingEntries.map((entry) => (
                      <div key={`review-${entry.id}`} className="flex flex-wrap items-center justify-between gap-2">
                        <span className="min-w-0 flex-1 truncate text-xs font-medium text-amber-900">
                          {entry.direction === "In" ? t("common.direction_in") : t("common.direction_out")} ·{" "}
                          {entry.failureReason}
                        </span>

                        <span className="flex gap-2">
                          <Button
                            typeButton="button"
                            label={t("management.approve")}
                            size="small"
                            bgColor={THEME.colors.buttonBGSaveColor}
                            iconColor={THEME.colors.iconBGSecondaryColor}
                            onClick={() => void handleReviewEntry(entry, true)}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.approveEntry"
                          />

                          <Button
                            typeButton="button"
                            label={t("management.reject")}
                            size="small"
                            bgColor={THEME.colors.buttonBGCloseColor}
                            iconColor={THEME.colors.buttonFGCloseColor}
                            onClick={() => void handleReviewEntry(entry, false)}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.rejectEntry"
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                </Animate>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <Animate type={AnimationTypes.slideUpIn} duration="1.9s">
                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="button"
                    label={t("management.period_entries")}
                    size="small"
                    bgColor={THEME.colors.buttonBGPlusColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    onClick={() => setIsEntriesOpen(true)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.openEntries"
                  />

                  <Button
                    typeButton="button"
                    label={t("management.edit_schedule")}
                    size="small"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    disabled={scheduleModel === "None"}
                    onClick={onEditSchedule}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.editSchedule"
                  />

                  <Button
                    typeButton="button"
                    label={t("management.define_pin")}
                    size="small"
                    bgColor={THEME.colors.buttonBGPlusColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    onClick={onEditPin}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.definePin"
                  />

                  <Button
                    typeButton="button"
                    label={t("management.new_adjustment")}
                    size="small"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    onClick={onCreateAdjustment}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.newAdjustment"
                  />

                  <Button
                    typeButton="button"
                    label={t("management.download_pdf")}
                    size="small"
                    bgColor={THEME.colors.buttonBGPlusColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    disabled={isLoading}
                    onClick={() => void downloadReportPdf(employee.employeeId, startDate, endDate)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockEmployeePopup.button.downloadPdf"
                  />
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>

      {isPeriodFilterOpen && (
        <TimeClockPeriodFilterPopup
          initialStartDate={startDate}
          initialEndDate={endDate}
          onClose={() => setIsPeriodFilterOpen(false)}
          onApply={(nextStartDate, nextEndDate) => {
            setStartDate(nextStartDate)
            setEndDate(nextEndDate)
            setIsPeriodFilterOpen(false)
          }}
        />
      )}

      {isEntriesOpen && (
        <TimeClockEmployeeEntriesPopup
          employeeName={employee.employeeName}
          cards={cards}
          onClose={() => setIsEntriesOpen(false)}
        />
      )}
    </ModalPortal>
  )
}
